import cl from 'classnames';
import { noop } from 'lodash';
import * as React from 'react';
import styled from 'styled-components';

import { deviceBreakpoints } from '../../../../hooks/useDeviceQuery.hook';
import { LayoutBox as Box } from '../../../Common/Box';
import { DOLTypographyVariant, Typography } from '../../../DesignSystemV2/Typography';
import { tableOfContentFlatMap } from './TableOfContent/utils';

export interface SectionOfContentProps extends React.HTMLAttributes<HTMLDivElement> {
  tableOfContent: any;
  sectionId: string;
  onTitleClick?: () => void;
  renderTitle?: (title: JSX.Element) => JSX.Element;
}

const Main = styled.section.withConfig({
  componentId: 'SectionOfContent_Main',
} as any)`
  .soc-title {
    display: flex;
    align-items: center;
    padding: 0 16px;
  }

  .soc-box {
    .soc-header {
      padding: 16px 24px;
    }

    .soc-content {
      padding: 24px;
    }

    @media ${deviceBreakpoints.mobile} {
      .soc-header {
        padding: 16px;
      }

      .soc-content {
        padding: 16px;
      }
    }
  }
`;

const MainPdf = styled(Main)`
  .soc-title {
    display: flex;
    align-items: center;
    padding: 0;
    margin-bottom: 16px;
  }
`;

interface GetSectionOfContentTitleParams {
  tableOfContent: any;
  sectionId: string;
  variant: DOLTypographyVariant;
  tabletVariant?: DOLTypographyVariant;
  iconPosition?: 'left' | 'right';
  withPrefix?: boolean;
  onClick?: () => void;
}

export const getSectionOfContentTitle = ({
  tableOfContent,
  sectionId,
  variant,
  tabletVariant,
  iconPosition = 'left',
  onClick = noop,
  withPrefix = true,
}: GetSectionOfContentTitleParams) => {
  let title;
  if (tableOfContent) {
    const section = tableOfContentFlatMap(tableOfContent).find((item) => sectionId === item.id);
    if (section) {
      title = (
        <Typography.Text
          onClick={onClick}
          className="soc-title"
          variant={variant}
          tabletVariant={tabletVariant || variant}
        >
          {withPrefix ? `${section.prefix} ${section.text}` : section.text}
        </Typography.Text>
      );
    }
  }
  return title;
};

export const SectionOfContent = (props: SectionOfContentProps) => {
  const {
    children,
    className,
    tableOfContent,
    sectionId,
    renderTitle,
    onTitleClick = noop,
    ...rest
  } = props;
  const title = getSectionOfContentTitle({
    tableOfContent,
    sectionId,
    variant: 'semi-bold/24',
    tabletVariant: 'semi-bold/32',
    onClick: onTitleClick,
  });

  return (
    <Main className={cl('soc-container', className)} {...rest}>
      {renderTitle ? renderTitle(title) : title}
      <Box.Container className="soc-box">{children}</Box.Container>
    </Main>
  );
};

export const SectionOfContentPdf = (props: SectionOfContentProps) => {
  const { children, className, tableOfContent, sectionId, renderTitle, ...rest } = props;
  const title = getSectionOfContentTitle({
    tableOfContent,
    sectionId,
    variant: 'semi-bold/24',
    tabletVariant: 'semi-bold/32',
    withPrefix: false,
  });

  return (
    <MainPdf className={cl('soc-container', className)} {...rest}>
      {renderTitle ? renderTitle(title) : title}
      <div className="soc-box">{children}</div>
    </MainPdf>
  );
};

SectionOfContent.Header = ({ children }) => (
  <Box.Header className="soc-header">{children}</Box.Header>
);

interface SectionOfContentContentProps {
  children: any;
  className?: string;
}

const BoxContent = styled.section`
  padding: 16px;
  display: grid;
  gap: 32px;
`;

SectionOfContent.Content = ({ children, className }: SectionOfContentContentProps) => (
  <BoxContent className={cl('soc-content', className)}>{children}</BoxContent>
);

SectionOfContent.ContentNoSpacing = ({ children, className }: SectionOfContentContentProps) => (
  <Box.Content className={cl('soc-content', className)}>{children}</Box.Content>
);
